/**
 * Exercise Modal – per-question check with immediate feedback and explanation.
 * No time limit, no passing score; learner checks each answer and sees explanation.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { X, CheckCircle2, XCircle, PenLine, ArrowLeft, ArrowRight, Loader2, Info } from 'lucide-react';
import { LessonExerciseHierarchyIcon } from '../admin/LessonExerciseHierarchyIcon';
import {
  contentService,
  CourseExerciseDto,
  LevelExerciseDto,
  LessonExerciseDto,
  ExerciseQuestionCheckResultDto,
  QuestionType
} from '../../services/contentService';
import { useNotifications } from '../feedback/NotificationSystem';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useLocalizedContent } from '../../hooks/useLocalizedContent';
import { localizeQuizExerciseQuestion, pickQuestionExplanation } from '../../utils/localizedContent';
import { useContentLocaleScope } from '../../hooks/useContentLocaleScope';
import { isDesignedForNonFarsiUsers } from '../../utils/nonFarsiContentHint';
import { EnglishLockedContentHintIcon } from '../shared/EnglishLockedContentHintIcon';
import { notifyGamificationAfterAction } from '../../utils/gamificationNotif';
import { isExercisePassed } from '../../utils/patchLessonProgress';
import { RichContentBlocksView } from './RichContentBlocksView';
import { FormattedTextBlock } from './FormattedTextBlock';
import { AudioPlayer } from '../shared/AudioPlayer';
import { FarsiKeyboardInput } from '../shared/FarsiKeyboardInput';
import { htmlContainsTerm } from '../../utils/searchHighlight';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';
import { isCommentQuestion, isPageSeparatorQuestion } from '../../utils/quizQuestionCount';
import { localeMirroredNavArrowClass } from '../../utils/localeNavArrow';
import { PersianText } from '../shared/PersianText';
import { LearnerSlideModalShell } from '../shared/LearnerSlideModalShell';
import { useLearnerSlideModalOnMount } from '../../hooks/useLearnerSlideModal';
import { PuzzleTouchDragGhost } from './PuzzleTouchDragGhost';
import {
  buildFindTheWordsHint,
  getCrosswordEntries,
  getFindTheWordsWords,
  getOptionPlainText,
  initialCrosswordAnswer,
  initialOrderFromOptions,
  parseFoundIds,
  resolveFindTheWordsGrid,
} from '../../utils/interactiveQuestionHelpers';
import { SortParagraphsPlayer } from './questionTypes/SortParagraphsPlayer';
import { EssayAnswerInput } from './questionTypes/EssayAnswerInput';
import { FindTheWordsPlayer, serializeFoundIds } from './questionTypes/FindTheWordsPlayer';
import { CrosswordPlayer } from './questionTypes/CrosswordPlayer';

/**
 * Search-highlight pulse keeps the same 5-second window used by the in-page
 * card flashes (`.search-result-highlight` in `index.css`). Defined here so we
 * can clear the question-level pulse class after the animation finishes.
 */
const SEARCH_HIGHLIGHT_DURATION_MS = 5000;

const EXERCISE_MODAL_PANEL_CLASS =
  'learner-run-sheet max-h-[min(90vh,820px)] w-full max-w-[min(48rem,calc(100vw-1.5rem))] border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900';

function portalExerciseModal(node: React.ReactNode): React.ReactNode {
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}

export type ExerciseType = 'course' | 'level' | 'lesson';

type UnifiedExerciseDto = CourseExerciseDto | LevelExerciseDto | LessonExerciseDto;

interface ExerciseModalProps {
  exerciseType: ExerciseType;
  exerciseId: number;
  onClose: () => void;
  /** Learner API (default) or admin API for lesson preview in content manager. */
  dataSource?: 'learner' | 'admin';
  /** Admin preview: show questions without check/submit. */
  readOnlyPreview?: boolean;
  /** Modal overlay (default) or embedded in lesson content. */
  variant?: 'modal' | 'inline';
  /** Optional block title shown above the exercise title when variant is inline. */
  inlineBlockTitle?: string;
  /** Set from practice exercise flow when the item is already marked reviewed. */
  markedAsReviewedByUser?: boolean;
  /**
   * When set (typically from the global learner-search), every question whose
   * stripped body contains this substring is flashed with the search-result
   * pulse and the term is wrapped with `<mark>` inside its FormattedTextBlock.
   */
  searchHighlightTerm?: string;
  /** Fired once when this search flash starts, so a later visit does not play it again. */
  onSearchHighlightConsumed?: () => void;
  /** Called after all lesson exercise answers are correct and completion is saved. */
  onLessonExerciseCompleted?: () => void;
  /** When true, exercise title/description and question bodies use English (page chrome stays in UI locale). */
  contentForceEnglish?: boolean;
  /** Opens the English-primary content hint (shown as a corner icon in fa UI). */
  onEnglishLockedHintClick?: () => void;
}

interface QuestionCheckState {
  questionId: number;
  correct: boolean;
  explanation?: string;
  explanationFa?: string;
}

export const ExerciseModal: React.FC<ExerciseModalProps> = ({
  exerciseType,
  exerciseId,
  onClose,
  dataSource = 'learner',
  readOnlyPreview = false,
  variant = 'modal',
  inlineBlockTitle,
  markedAsReviewedByUser = false,
  searchHighlightTerm,
  onSearchHighlightConsumed,
  onLessonExerciseCompleted,
  contentForceEnglish = false,
  onEnglishLockedHintClick,
}) => {
  const isInline = variant === 'inline';
  const isAdminPreview = readOnlyPreview && dataSource === 'admin';
  const { showError, showSuccess } = useNotifications();
  const { t } = useTranslation(['learning', 'notifications']);
  const { direction } = useAppLocale();
  const [exercise, setExercise] = useState<UnifiedExerciseDto | null>(null);
  const exerciseEnglishLocked =
    contentForceEnglish || (exercise != null && isDesignedForNonFarsiUsers(exercise));
  const { pickTitle, pickDescription, locale } = useLocalizedContent({
    forceEnglish: exerciseEnglishLocked,
  });
  const contentIsRtl = direction === 'rtl';
  const navArrowClass = (size: string) => localeMirroredNavArrowClass(contentIsRtl, size);
  const exerciseTitle = exercise ? pickTitle(exercise) : '';
  const exerciseDescription = exercise ? pickDescription(exercise) : '';
  const { tContent } = useContentLocaleScope(exerciseEnglishLocked, [
    'learning',
    'common',
    'notifications',
  ]);
  const contentInputDir = exerciseEnglishLocked ? 'ltr' : 'rtl';
  /** Modal header and chrome follow UI locale. */
  const tc = t;
  /** Question-phase strings when content is English-locked. */
  const tcQ = exerciseEnglishLocked ? tContent : t;
  const formattedTextDir = exerciseEnglishLocked ? ('ltr' as const) : undefined;
  const questionContentLocale = exerciseEnglishLocked ? ('en' as const) : locale;
  const [passedLottieUnavailable, setPassedLottieUnavailable] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [checkResults, setCheckResults] = useState<Record<number, QuestionCheckState>>({});
  const [checkingQuestionId, setCheckingQuestionId] = useState<number | null>(null);
  const [puzzleOrders, setPuzzleOrders] = useState<Record<number, number[]>>({});
  const [puzzleDragging, setPuzzleDragging] = useState<Record<number, number | null>>({});
  const [puzzleDragPreviewIndex, setPuzzleDragPreviewIndex] = useState<{ questionId: number; index: number } | null>(null);
  // Floating "ghost" that follows the finger during a touch drag so the
  // dragged card visually sticks to the finger immediately (instead of
  // waiting for the finger to cross into another card before any visual
  // movement happens).
  const [puzzleTouchGhost, setPuzzleTouchGhost] = useState<{
    questionId: number;
    optionId: number;
    clientX: number;
    clientY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
  } | null>(null);
  const puzzleDraggedIdRef = useRef<number | null>(null);
  const puzzleDragOverIndexRef = useRef<{ questionId: number; index: number } | null>(null);
  const puzzleTouchQuestionIdRef = useRef<number | null>(null);
  const puzzleTouchOrderRef = useRef<number[]>([]);
  const puzzleDocTouchCleanupRef = useRef<(() => void) | null>(null);
  const exerciseSessionStartRef = useRef<number | null>(null);
  const exerciseTimeFlushSubmittedRef = useRef(false);
  const lessonCompletionRecordedRef = useRef(false);
  /**
   * Map of `questionId → ref` used by the global-search auto-scroll/flash.
   * Populated lazily during render and read inside the post-load effect.
   */
  const questionRefsRef = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const [pulsingQuestionIds, setPulsingQuestionIds] = useState<Set<number>>(() => new Set());
  const highlightTimerRef = useRef<number | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const flushExerciseSessionTime = useCallback(async () => {
    if (isAdminPreview || exerciseTimeFlushSubmittedRef.current || exerciseSessionStartRef.current == null) return;
    exerciseTimeFlushSubmittedRef.current = true;
    const started = exerciseSessionStartRef.current;
    exerciseSessionStartRef.current = null;
    const secs = Math.min(86400, Math.max(0, Math.floor((Date.now() - started) / 1000)));
    if (secs <= 0) return;
    try {
      await contentService.recordExerciseTimeSession({
        scope: exerciseType,
        exerciseId,
        secondsSpent: secs
      });
    } catch {
      // metrics only
    }
  }, [exerciseType, exerciseId, isAdminPreview]);

  const finishExerciseClose = useCallback(() => {
    void flushExerciseSessionTime();
    onClose();
  }, [flushExerciseSessionTime, onClose]);

  const {
    mounted: slideMounted,
    visible: slideVisible,
    panelRef: slidePanelRef,
    requestClose: requestExerciseClose,
    handleExitTransitionEnd,
  } = useLearnerSlideModalOnMount(finishExerciseClose, undefined, {
    historyBackId: 'exercise-modal',
  });

  const handleDismiss = requestExerciseClose;

  const handlePuzzleWordDrag = useCallback((questionId: number, dragIndex: number, dropIndex: number) => {
    if (readOnlyPreview) return;
    setCheckResults(prev => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    setPuzzleOrders(prev => {
      const currentOrder = prev[questionId] || [];
      const newOrder = [...currentOrder];
      const [dragged] = newOrder.splice(dragIndex, 1);
      if (dragged === undefined) return prev;
      newOrder.splice(dropIndex, 0, dragged);
      const answerString = newOrder.map(id => id.toString()).join(',');
      setSelectedAnswers(sa => ({ ...sa, [questionId]: answerString }));
      return { ...prev, [questionId]: newOrder };
    });
  }, []);

  const getPuzzleDropTarget = useCallback((clientX: number, clientY: number): { questionId: number; index: number } | null => {
    let el: Element | null = document.elementFromPoint(clientX, clientY);
    while (el && !(el as HTMLElement).dataset?.puzzleOptionId) el = el.parentElement;
    if (!el) return null;
    const qid = (el as HTMLElement).dataset.puzzleQuestionId;
    const idxStr = (el as HTMLElement).dataset.puzzleIndex;
    const optionIdStr = (el as HTMLElement).dataset.puzzleOptionId;
    if (qid == null || idxStr == null) return null;
    const questionId = parseInt(qid, 10);
    const index = parseInt(idxStr, 10);
    const optionId = optionIdStr != null ? parseInt(optionIdStr, 10) : null;
    if (optionId !== null && optionId === puzzleDraggedIdRef.current) return null;
    return { questionId, index };
  }, []);

  const puzzleDocPointerMove = useCallback((e: PointerEvent) => {
    if (puzzleDraggedIdRef.current == null) return;
    e.preventDefault();
    setPuzzleTouchGhost(prev =>
      prev ? { ...prev, clientX: e.clientX, clientY: e.clientY } : prev
    );
    const target = getPuzzleDropTarget(e.clientX, e.clientY);
    if (!target || target.questionId !== puzzleTouchQuestionIdRef.current) return;
    puzzleDragOverIndexRef.current = target;
    setPuzzleDragPreviewIndex(prev => (prev?.questionId === target.questionId && prev?.index === target.index ? prev : target));
  }, [getPuzzleDropTarget]);

  const puzzleDocPointerEnd = useCallback((e: PointerEvent) => {
    const cleanup = puzzleDocTouchCleanupRef.current;
    if (cleanup) {
      puzzleDocTouchCleanupRef.current = null;
      cleanup();
    }
    const questionId = puzzleTouchQuestionIdRef.current;
    const order = puzzleTouchOrderRef.current;
    const draggedId = puzzleDraggedIdRef.current;
    let over = puzzleDragOverIndexRef.current;
    if (questionId != null && draggedId != null && order.length) {
      over = getPuzzleDropTarget(e.clientX, e.clientY);
    }
    puzzleDraggedIdRef.current = null;
    puzzleTouchQuestionIdRef.current = null;
    puzzleDragOverIndexRef.current = null;
    setPuzzleDragPreviewIndex(null);
    setPuzzleTouchGhost(null);
    if (questionId != null) setPuzzleDragging(prev => ({ ...prev, [questionId]: null }));
    if (questionId == null || draggedId == null || !order.length || over?.questionId !== questionId || over?.index == null) return;
    const dragIndex = order.indexOf(draggedId);
    if (dragIndex >= 0 && dragIndex !== over.index) handlePuzzleWordDrag(questionId, dragIndex, over.index);
  }, [handlePuzzleWordDrag, getPuzzleDropTarget]);

  useEffect(() => {
    loadExercise();
    return () => {
      void flushExerciseSessionTime();
    };
  }, [exerciseType, exerciseId, dataSource, flushExerciseSessionTime]);

  useEffect(() => {
    if (!markedAsReviewedByUser) return;
    fetch('/animations/Passed.lottie', { method: 'HEAD' })
      .then((r) => {
        if (!r.ok) setPassedLottieUnavailable(true);
      })
      .catch(() => setPassedLottieUnavailable(true));
  }, [markedAsReviewedByUser]);

  // Flash and scroll to the question(s) matching the global learner-search
  // term. Runs every time the modal finishes loading a new exercise (and when
  // the active term changes).
  const searchFlashPlayedRef = useRef(false);

  useEffect(() => {
    if (searchFlashPlayedRef.current) return;
    if (!exercise?.questions?.length) return;
    const term = searchHighlightTerm?.trim();
    if (!term) return;

    const matching = (exercise.questions as any[]).filter((q) => {
      const inBody = htmlContainsTerm(q.textContent ?? '', term);
      if (inBody) return true;
      const opts = q.questionOptions ?? [];
      return opts.some((o: any) => htmlContainsTerm(o.textContent ?? '', term));
    });
    searchFlashPlayedRef.current = true;
    onSearchHighlightConsumed?.();
    if (matching.length === 0) return;

    setPulsingQuestionIds(new Set(matching.map((q) => q.id as number)));

    // Defer scroll until the question elements have been mounted via render.
    window.requestAnimationFrame(() => {
      const first = matching[0];
      const el = first ? questionRefsRef.current.get(first.id) : null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    if (highlightTimerRef.current != null) window.clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = window.setTimeout(() => {
      setPulsingQuestionIds(new Set());
      highlightTimerRef.current = null;
    }, SEARCH_HIGHLIGHT_DURATION_MS);
  }, [exercise, searchHighlightTerm, onSearchHighlightConsumed]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!exercise?.questions?.length) return;
    const nextOrders: Record<number, number[]> = {};
    const nextAnswers: Record<number, string> = {};
    (exercise.questions as any[]).forEach((q) => {
      const qt = q.questionType;
      if (
        (qt === QuestionType.Puzzle || qt === QuestionType.SortParagraphs) &&
        q.questionOptions?.length
      ) {
        const order = initialOrderFromOptions(q.questionOptions);
        if (order.length) {
          nextOrders[q.id] = order;
          nextAnswers[q.id] = order.join(',');
        }
      } else if (qt === QuestionType.Essay) {
        nextAnswers[q.id] = '';
      } else if (qt === QuestionType.FindTheWords) {
        nextAnswers[q.id] = '';
      } else if (qt === QuestionType.Crossword) {
        nextAnswers[q.id] = initialCrosswordAnswer(q);
      }
    });
    setPuzzleOrders(nextOrders);
    setSelectedAnswers((prev) => ({ ...prev, ...nextAnswers }));
  }, [exercise]);

  const loadExercise = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      let data: UnifiedExerciseDto;
      switch (exerciseType) {
        case 'course':
          data = await contentService.getCourseExerciseForLearner(exerciseId);
          break;
        case 'level':
          data = await contentService.getLevelExerciseForLearner(exerciseId);
          break;
        case 'lesson':
          data =
            dataSource === 'admin'
              ? await contentService.getLessonExerciseById(exerciseId)
              : await contentService.getLessonExerciseForLearner(exerciseId);
          break;
        default:
          throw new Error(`Unsupported exercise type: ${exerciseType}`);
      }
      setExercise({
        ...data,
        isDesignedForNonFarsiUsers: isDesignedForNonFarsiUsers(data),
      });
      setSelectedAnswers({});
      setPuzzleOrders({});
      setCheckResults({});
      lessonCompletionRecordedRef.current = false;
      exerciseTimeFlushSubmittedRef.current = false;
      exerciseSessionStartRef.current = Date.now();
      setCurrentPageIndex(0);
    } catch (err) {
      exerciseSessionStartRef.current = null;
      exerciseTimeFlushSubmittedRef.current = false;
      const message = err instanceof Error ? err.message : tc('exercise.loadFailed');
      if (!isInline) {
        showError(message);
        onClose();
      } else {
        setExercise(null);
        setLoadError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (questionId: number, optionId: number) => {
    if (readOnlyPreview) return;
    setSelectedAnswers(prev => ({ ...prev, [questionId]: String(optionId) }));
    setCheckResults(prev => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const handleFillInBlankChange = (questionId: number, value: string) => {
    if (readOnlyPreview) return;
    setSelectedAnswers(prev => ({ ...prev, [questionId]: value }));
    setCheckResults(prev => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const handleCheck = async (questionId: number) => {
    if (readOnlyPreview) return;
    const question = (exercise?.questions as any[] | undefined)?.find((q) => q.id === questionId);
    let answer = selectedAnswers[questionId];
    // Crossword: always send a full rows×cols grid string (pad from blank mask if needed).
    if (question && question.questionType === QuestionType.Crossword) {
      const blank = initialCrosswordAnswer(question);
      if (blank) {
        const chars = String(answer ?? '').split('');
        answer = blank
          .split('')
          .map((ch: string, i: number) => {
            if (ch === '#') return '#';
            const u = chars[i];
            if (u == null || u === '#' || u === '' || u === ' ') return '.';
            return u;
          })
          .join('');
      }
    }
    if (answer == null || answer.trim() === '') {
      showError(tc('exercise.selectAnswerFirst'));
      return;
    }
    try {
      setCheckingQuestionId(questionId);
      let result: ExerciseQuestionCheckResultDto;
      switch (exerciseType) {
        case 'course':
          result = await contentService.checkCourseExerciseQuestion(exerciseId, questionId, answer);
          break;
        case 'level':
          result = await contentService.checkLevelExerciseQuestion(exerciseId, questionId, answer);
          break;
        case 'lesson':
          result = await contentService.checkLessonExerciseQuestion(exerciseId, questionId, answer);
          break;
        default:
          throw new Error(`Unsupported exercise type: ${exerciseType}`);
      }
      setCheckResults(prev => ({
        ...prev,
        [questionId]: {
          questionId,
          correct: result.correct,
          explanation: result.explanation ?? undefined,
          explanationFa: result.explanationFa ?? undefined,
        }
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : tc('exercise.checkFailed');
      showError(message);
    } finally {
      setCheckingQuestionId(null);
    }
  };

  const scorableQuestions = useMemo(() => {
    if (!exercise?.questions) return [];
    return exercise.questions
      .filter((q: any) => !isPageSeparatorQuestion(q) && !isCommentQuestion(q))
      .map((q) => localizeQuizExerciseQuestion(q, questionContentLocale));
  }, [exercise?.questions, questionContentLocale]);

  const activeQuestions = useMemo(
    () => ((exercise?.questions as any[]) ?? []).map((q) => localizeQuizExerciseQuestion(q, questionContentLocale)),
    [exercise?.questions, questionContentLocale]
  );

  const { answerableQuestions, pages, hasMultiplePages } = useMemo(() => {
    const questions = activeQuestions;
    const real = questions.filter((q: any) => !isPageSeparatorQuestion(q));
    const answerable = real.filter((q: any) => !isCommentQuestion(q));
    const pageChunks: number[][] = [];
    let chunk: number[] = [];
    questions.forEach((q: any, idx: number) => {
      if (isPageSeparatorQuestion(q)) {
        if (chunk.length) {
          pageChunks.push(chunk);
          chunk = [];
        }
      } else {
        chunk.push(idx);
      }
    });
    if (chunk.length) pageChunks.push(chunk);
    return {
      answerableQuestions: answerable,
      pages: pageChunks.length ? pageChunks : [real.map((_: any, i: number) => i)],
      hasMultiplePages: pageChunks.length > 1,
    };
  }, [activeQuestions]);

  const questionsToShow = useMemo(() => {
    if (!activeQuestions.length) return [];
    if (hasMultiplePages) {
      const indices = pages[currentPageIndex] ?? [];
      return indices.map((i: number) => activeQuestions[i]);
    }
    return activeQuestions.filter((q: any) => !isPageSeparatorQuestion(q));
  }, [activeQuestions, hasMultiplePages, pages, currentPageIndex]);

  useEffect(() => {
    if (exerciseType !== 'lesson' || scorableQuestions.length === 0) return;
    const allCorrect = scorableQuestions.every((q) => checkResults[q.id]?.correct);
    if (!allCorrect || lessonCompletionRecordedRef.current) return;

    lessonCompletionRecordedRef.current = true;
    const answers = scorableQuestions.map((q) => ({
      questionId: q.id,
      answer: selectedAnswers[q.id] ?? ''
    }));

    void contentService
      .completeLessonExercise(exerciseId, { answers })
      .then((res) => {
        if (isExercisePassed(res)) {
          showSuccess(t('notifications:exercise.completed'));
          notifyGamificationAfterAction();
          onLessonExerciseCompleted?.();
        } else {
          lessonCompletionRecordedRef.current = false;
        }
      })
      .catch((err) => {
        lessonCompletionRecordedRef.current = false;
        showError(err instanceof Error ? err.message : tc('exercise.recordFailed'));
      });
  }, [
    checkResults,
    selectedAnswers,
    exerciseType,
    exerciseId,
    scorableQuestions,
    onLessonExerciseCompleted,
    showError,
    showSuccess
  ]);

  if (loading) {
    const loadingBody = (
      <div
        dir={direction}
        className="flex min-h-[min(40vh,280px)] flex-col items-center justify-center gap-3 p-8"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-8 w-8 animate-spin text-amber-600 dark:text-amber-400" aria-hidden />
        <p className="text-sm text-gray-600 dark:text-gray-400">{tc('exercise.loading')}</p>
      </div>
    );
    if (isInline) {
      return (
        <div className="rounded-2xl border border-amber-200/85 bg-white/90 dark:border-amber-900/35 dark:bg-slate-900/50">
          {loadingBody}
        </div>
      );
    }
    return portalExerciseModal(
      <LearnerSlideModalShell
        mounted={slideMounted}
        visible={slideVisible}
        panelRef={slidePanelRef}
        onBackdropClick={handleDismiss}
        onPanelTransitionEnd={handleExitTransitionEnd}
        panelClassName={EXERCISE_MODAL_PANEL_CLASS}
        ariaLabelledBy="exercise-modal-title"
        align="center"
      >
        {loadingBody}
      </LearnerSlideModalShell>
    );
  }

  if (!exercise) {
    const message = loadError ?? tc('exercise.notFound');
    if (isInline) {
      return (
        <div className="rounded-2xl border border-amber-200/85 bg-amber-50/50 p-6 text-center dark:border-amber-900/35 dark:bg-amber-950/20">
          <p className="text-sm text-gray-600 dark:text-gray-300">{message}</p>
        </div>
      );
    }
    return portalExerciseModal(
      <LearnerSlideModalShell
        mounted={slideMounted}
        visible={slideVisible}
        panelRef={slidePanelRef}
        onBackdropClick={handleDismiss}
        onPanelTransitionEnd={handleExitTransitionEnd}
        panelClassName={EXERCISE_MODAL_PANEL_CLASS}
        ariaLabelledBy="exercise-modal-title"
        align="center"
      >
        <div className="flex flex-col items-center justify-center p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">{message}</p>
          <button
            type="button"
            onClick={handleDismiss}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </LearnerSlideModalShell>
    );
  }

  const questions = questionsToShow;

  const normalizeImageUrl = normalizeAppHostedUploadUrl;

  const panel = (
      <div
        className={
          isInline
            ? 'rounded-2xl border border-amber-200/85 bg-gradient-to-br from-white via-amber-50/40 to-orange-50/25 shadow-sm dark:border-amber-900/35 dark:from-slate-900 dark:via-amber-950/15 dark:to-slate-950 overflow-hidden flex flex-col'
            : 'flex flex-col overflow-hidden overflow-x-hidden max-h-[inherit]'
        }
        dir={direction}
      >
        {isInline && markedAsReviewedByUser ? (
          <div className="flex shrink-0 items-center gap-2 px-4 pt-3 pb-1">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center" aria-hidden>
              {passedLottieUnavailable ? (
                <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
              ) : (
                <DotLottieReact src="/animations/Passed.lottie" loop={false} autoplay className="h-full w-full" />
              )}
            </div>
            <p className="text-sm font-medium text-green-600 dark:text-green-400">
              You passed this practice
            </p>
          </div>
        ) : null}
        {/* Header */}
        <div className="flex flex-shrink-0 items-start gap-3 border-b border-amber-200/60 bg-amber-50/50 px-3 py-3 dark:border-amber-900/40 dark:bg-amber-950/20 sm:px-4">
          <div className="mt-0.5 shrink-0 rounded-lg bg-amber-100 p-2 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            {isInline ? (
              <LessonExerciseHierarchyIcon inline className="h-5 w-5" />
            ) : (
              <PenLine className="h-5 w-5" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            {isInline ? (
              <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800/90 dark:text-amber-300/90">
                Inline Exercise
              </p>
            ) : exerciseType === 'lesson' ? (
              <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800/90 dark:text-amber-300/90">
                {tc('exercise.label', { defaultValue: 'Exercise' })}
              </p>
            ) : inlineBlockTitle ? (
              <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800/90 dark:text-amber-300/90">
                {inlineBlockTitle}
              </p>
            ) : null}
            <h2 id="exercise-modal-title" className="text-lg font-bold leading-snug text-gray-900 dark:text-gray-100">
              {!exerciseEnglishLocked && locale === 'fa' ? (
                <PersianText>{exerciseTitle}</PersianText>
              ) : (
                exerciseTitle
              )}
            </h2>
            {isAdminPreview && (
              <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-300/90">
                Admin preview — learners can check answers when the lesson is published.
              </p>
            )}
            {markedAsReviewedByUser && !isInline ? (
              <div className="mt-2 flex min-w-0 items-center gap-2 rounded-lg border border-green-200/90 bg-green-50/95 px-3 py-2 dark:border-green-700/55 dark:bg-emerald-900/55">
                <span className="min-w-0 flex-1 text-xs font-medium leading-snug text-green-700 dark:text-green-400">
                  You marked this exercise as reviewed.
                </span>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center">
                  {passedLottieUnavailable ? (
                    <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" aria-hidden />
                  ) : (
                    <DotLottieReact src="/animations/Passed.lottie" loop={false} autoplay className="h-full w-full" />
                  )}
                </div>
              </div>
            ) : null}
            {exerciseDescription ? (
              <p className="mt-1.5 text-xs leading-relaxed text-justify text-gray-600 dark:text-gray-400">
                {locale === 'fa' ? (
                  <PersianText>{exerciseDescription}</PersianText>
                ) : (
                  exerciseDescription
                )}
              </p>
            ) : null}
            {!isInline ? (
              <div
                className={`flex gap-2 rounded-lg border border-amber-200/90 bg-amber-100/55 px-3 py-2.5 dark:border-amber-800/50 dark:bg-amber-950/40 ${
                  exerciseDescription || markedAsReviewedByUser ? 'mt-3' : 'mt-2'
                }`}
                {...(locale === 'fa' ? { dir: 'rtl' as const } : {})}
              >
                <Info
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-300"
                  aria-hidden
                />
                <p className="min-w-0 text-[10px] leading-relaxed text-justify text-amber-950/80 dark:text-amber-100/80">
                  {tc('exercise.instructionsHint')}
                </p>
              </div>
            ) : null}
          </div>
          {!isInline ? (
            <div className="flex shrink-0 flex-col items-center gap-0.5">
              <button
                type="button"
                onClick={handleDismiss}
                className="self-start rounded-lg p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              {onEnglishLockedHintClick ? (
                <EnglishLockedContentHintIcon onClick={onEnglishLockedHintClick} />
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Questions */}
        <div
          className="min-w-0 max-h-[min(calc(90vh-8rem),740px)] space-y-6 overflow-y-auto overflow-x-hidden p-4 sm:p-6"
        >
          {questions.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No questions in this exercise.</p>
          )}
          {questions.map((q: any, index: number) => {
            const options = q.questionOptions ?? [];
            const selected = selectedAnswers[q.id];
            const result = checkResults[q.id];
            const isChecking = checkingQuestionId === q.id;
            const isHighlighted = pulsingQuestionIds.has(q.id);

            if (isCommentQuestion(q)) {
              return (
                <div
                  key={q.id}
                  ref={(el) => { questionRefsRef.current.set(q.id, el); }}
                  className={`rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/80 dark:bg-purple-900/20 p-4 ${
                    isHighlighted ? 'search-result-highlight scroll-mt-24' : ''
                  }`}
                >
                  <RichContentBlocksView
                    source={q}
                    imageAlt="Comment"
                    contentDirection={formattedTextDir}
                    {...(isHighlighted && searchHighlightTerm ? { searchHighlightTerm } : {})}
                  />
                </div>
              );
            }

            return (
              <div
                key={q.id}
                ref={(el) => { questionRefsRef.current.set(q.id, el); }}
                dir={formattedTextDir}
                className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 p-4 ${
                  exerciseEnglishLocked ? 'text-start' : ''
                } ${
                  isHighlighted ? 'search-result-highlight scroll-mt-24' : ''
                }`}
              >
                <div className="flex items-start gap-2 mb-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 flex items-center justify-center text-sm font-semibold">
                    {answerableQuestions.findIndex((aq: any) => aq.id === q.id) + 1 || index + 1}
                  </span>
                  <div className="flex-1 min-w-0 text-gray-900 dark:text-gray-100">
                    <RichContentBlocksView
                      source={q}
                      imageAlt="Question"
                      contentDirection={formattedTextDir}
                      {...(isHighlighted && searchHighlightTerm ? { searchHighlightTerm } : {})}
                    />
                    {!q.textContent?.trim()
                      && !(q as any).imageUrl?.trim()
                      && !(q as any).audioUrl?.trim()
                      && !(q as any).videoUrl?.trim()
                      && !(q as any).textFormatting?.trim()
                      && (q as any).question ? (
                      <h3 dir={formattedTextDir} className={`mb-2${exerciseEnglishLocked ? ' text-start' : ''}`}>
                        {(q as any).question}
                      </h3>
                    ) : null}
                  </div>
                </div>

                {q.questionType === QuestionType.FillInTheBlank
                  ? (
                    <div className="mt-3">
                      <FarsiKeyboardInput
                        value={selected ?? ''}
                        onChange={(v) => handleFillInBlankChange(q.id, v)}
                        keyboardPosition="fixed-above-anchor"
                        type="text"
                        readOnly={readOnlyPreview}
                        className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder={readOnlyPreview ? '' : tcQ('exercise.answerPlaceholder')}
                        dir={contentInputDir}
                        style={{ direction: contentInputDir, textAlign: contentInputDir === 'rtl' ? 'right' : 'left' }}
                      />
                    </div>
                    )
                  : q.questionType === QuestionType.Puzzle && options.length > 0
                  ? (() => {
                      const questionId = q.id;
                      const currentOrder =
                        puzzleOrders[questionId] ||
                        [...options]
                          .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                          .map((opt: any) => opt.id ?? 0);
                      const optionMap = new Map<number, any>(options.map((opt: any) => [opt.id ?? 0, opt]));
                      const puzzleAlignment = exerciseEnglishLocked
                        ? 'ltr'
                        : String(q.puzzleAlignment || 'rtl').toLowerCase() === 'ltr'
                          ? 'ltr'
                          : 'rtl';
                      const draggedOptionId = puzzleDragging[questionId] ?? null;
                      const touchGhostForQuestion =
                        puzzleTouchGhost && puzzleTouchGhost.questionId === questionId ? puzzleTouchGhost : null;
                      const extractWordText = (opt: any, fallbackIndex: number): string => {
                        if (opt?.textContent) {
                          const temp = document.createElement('div');
                          temp.innerHTML = opt.textContent;
                          return temp.textContent || temp.innerText || `Word ${fallbackIndex + 1}`;
                        }
                        return `Word ${fallbackIndex + 1}`;
                      };
                      const orderForRender =
                        puzzleDragPreviewIndex?.questionId === questionId && puzzleDragPreviewIndex?.index !== undefined && draggedOptionId != null
                          ? (() => {
                              const base = puzzleOrders[questionId] ?? currentOrder;
                              const dragIdx = base.indexOf(draggedOptionId);
                              if (dragIdx < 0) return base;
                              const reordered = [...base];
                              reordered.splice(dragIdx, 1);
                              reordered.splice(puzzleDragPreviewIndex.index, 0, draggedOptionId);
                              return reordered;
                            })()
                          : currentOrder;
                      return (
                        <div className="space-y-2 mt-3">
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {tcQ('interactiveQuestions.puzzleHint', {
                              defaultValue: 'Drag the words below to arrange them in the correct order.',
                            })}
                          </p>
                          <div
                            className="flex flex-wrap gap-2 sm:gap-1.5 p-2.5 sm:p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 w-fit max-w-full"
                            style={{
                              direction: 'ltr',
                              flexDirection: puzzleAlignment === 'rtl' ? 'row-reverse' : 'row',
                            }}
                          >
                            {orderForRender.map((optionId: number, idx: number) => {
                              const opt = optionMap.get(optionId);
                              if (!opt) return null;
                              const wordText = extractWordText(opt, idx);
                              const isDragging = draggedOptionId === optionId;
                              const isTouchGhosted = touchGhostForQuestion?.optionId === optionId;
                              return (
                                <div
                                  key={optionId}
                                  data-puzzle-option-id={optionId}
                                  data-puzzle-question-id={questionId}
                                  data-puzzle-index={idx}
                                  onPointerDown={(e) => {
                                    if (readOnlyPreview || e.button !== 0) return;
                                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                    puzzleDraggedIdRef.current = optionId;
                                    puzzleTouchQuestionIdRef.current = questionId;
                                    puzzleTouchOrderRef.current = puzzleOrders[questionId] ?? currentOrder;
                                    setPuzzleDragging(prev => ({ ...prev, [questionId]: optionId }));
                                    setPuzzleDragPreviewIndex(null);
                                    setPuzzleTouchGhost({
                                      questionId,
                                      optionId,
                                      clientX: e.clientX,
                                      clientY: e.clientY,
                                      offsetX: e.clientX - rect.left,
                                      offsetY: e.clientY - rect.top,
                                      width: rect.width,
                                      height: rect.height,
                                    });
                                    const cleanup = () => {
                                      document.removeEventListener('pointermove', puzzleDocPointerMove);
                                      document.removeEventListener('pointerup', puzzleDocPointerEnd);
                                      document.removeEventListener('pointercancel', puzzleDocPointerEnd);
                                    };
                                    puzzleDocTouchCleanupRef.current = cleanup;
                                    document.addEventListener('pointermove', puzzleDocPointerMove);
                                    document.addEventListener('pointerup', puzzleDocPointerEnd);
                                    document.addEventListener('pointercancel', puzzleDocPointerEnd);
                                    e.preventDefault();
                                  }}
                                  className={`inline-flex items-center px-3 py-1.5 sm:px-2 sm:py-1 rounded-md cursor-move transition-colors touch-none select-none ${
                                    isDragging
                                      ? 'bg-amber-600 dark:bg-amber-700 text-white border border-amber-700 dark:border-amber-600 shadow-md scale-105 z-50'
                                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-amber-300 dark:border-amber-600 hover:border-amber-500 dark:hover:border-amber-400 hover:shadow-sm'
                                  }`}
                                  style={{
                                    touchAction: 'none',
                                    width: 'fit-content',
                                    height: 'fit-content',
                                    // While touch-dragging, the floating ghost
                                    // shows the card at the finger position;
                                    // hide the in-flow copy to avoid a duplicate.
                                    visibility: isTouchGhosted ? 'hidden' : undefined,
                                  }}
                                >
                                  <span className="font-medium text-sm leading-tight whitespace-nowrap" dir="auto">
                                    {wordText}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {touchGhostForQuestion && (() => {
                            const ghostOpt = optionMap.get(touchGhostForQuestion.optionId);
                            if (!ghostOpt) return null;
                            return (
                              <PuzzleTouchDragGhost
                                ghost={touchGhostForQuestion}
                                wordText={extractWordText(ghostOpt, 0)}
                                className="inline-flex items-center justify-center px-3 py-1.5 sm:px-2 sm:py-1 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-amber-300 dark:border-amber-600 shadow-md select-none"
                              />
                            );
                          })()}
                        </div>
                      );
                    })()
                  : q.questionType === QuestionType.SortParagraphs && options.length > 0
                  ? (() => {
                      const questionId = q.id;
                      const currentOrder =
                        puzzleOrders[questionId] || initialOrderFromOptions(options);
                      const sortAlignment = exerciseEnglishLocked
                        ? 'ltr'
                        : String(q.puzzleAlignment || 'rtl').toLowerCase() === 'ltr'
                          ? 'ltr'
                          : 'rtl';
                      return (
                        <div className="mt-3">
                          <SortParagraphsPlayer
                            questionId={questionId}
                            options={options}
                            order={currentOrder}
                            alignment={sortAlignment}
                            disabled={readOnlyPreview}
                            hint={tcQ('interactiveQuestions.sortParagraphsHint', {
                              defaultValue:
                                'Drag the paragraphs or use the arrows to put them in the correct order.',
                            })}
                            onOrderChange={(newOrder) => {
                              if (readOnlyPreview) return;
                              setCheckResults((prev) => {
                                const next = { ...prev };
                                delete next[questionId];
                                return next;
                              });
                              setPuzzleOrders((prev) => ({ ...prev, [questionId]: newOrder }));
                              setSelectedAnswers((sa) => ({
                                ...sa,
                                [questionId]: newOrder.map((id) => String(id)).join(','),
                              }));
                            }}
                          />
                        </div>
                      );
                    })()
                  : q.questionType === QuestionType.Essay
                  ? (
                    <div className="mt-3">
                      <EssayAnswerInput
                        value={selected ?? ''}
                        onChange={(v) => handleFillInBlankChange(q.id, v)}
                        disabled={readOnlyPreview}
                        minLength={(q as any).minAnswerLength}
                        maxLength={(q as any).maxAnswerLength}
                        textDirection={
                          String((q as any).puzzleAlignment || 'rtl').toLowerCase() === 'ltr'
                            ? 'ltr'
                            : 'rtl'
                        }
                        placeholder={readOnlyPreview ? '' : tcQ('exercise.answerPlaceholder')}
                      />
                    </div>
                    )
                  : q.questionType === QuestionType.FindTheWords
                  ? (() => {
                      const questionId = q.id;
                      const { gridLetters, rows, cols } = resolveFindTheWordsGrid(q);
                      const words = getFindTheWordsWords(options);
                      const foundIds = parseFoundIds(selected);
                      const qAny = q as any;
                      return (
                        <div className="mt-3">
                          <FindTheWordsPlayer
                            gridLetters={gridLetters}
                            rows={rows}
                            cols={cols}
                            words={words}
                            foundIds={foundIds}
                            disabled={readOnlyPreview}
                            hint={buildFindTheWordsHint(
                              {
                                allowHorizontal: qAny.allowHorizontal,
                                allowVertical: qAny.allowVertical,
                                allowDiagonal: qAny.allowDiagonal,
                              },
                              tcQ,
                              contentIsRtl
                            )}
                            onFoundIdsChange={(ids) => {
                              if (readOnlyPreview) return;
                              handleFillInBlankChange(questionId, serializeFoundIds(ids));
                            }}
                          />
                        </div>
                      );
                    })()
                  : q.questionType === QuestionType.Crossword
                  ? (() => {
                      const questionId = q.id;
                      const rows = Number((q as any).gridRows) || 0;
                      const cols = Number((q as any).gridCols) || 0;
                      const entries = getCrosswordEntries(options);
                      const blank = initialCrosswordAnswer(q);
                      return (
                        <div className="mt-3">
                          <CrosswordPlayer
                            rows={rows}
                            cols={cols}
                            entries={entries}
                            value={selected || blank}
                            disabled={readOnlyPreview}
                            hint={tcQ('interactiveQuestions.crosswordHint', {
                              defaultValue:
                                'Tap a cell and type. Use the clues to fill the crossword.',
                            })}
                            onChange={(v) => handleFillInBlankChange(questionId, v)}
                          />
                        </div>
                      );
                    })()
                  : options.length > 0 ? (
                  <div className="space-y-2 mt-3">
                    {options
                      .slice()
                      .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                      .map((opt: any) => (
                        <button
                          key={opt.id}
                          type="button"
                          disabled={readOnlyPreview}
                          onClick={() => handleSelectAnswer(q.id, opt.id)}
                          className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                            exerciseEnglishLocked ? 'text-start' : 'text-left'
                          } ${
                            selected === String(opt.id)
                              ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/25 dark:border-amber-500'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                          }`}
                        >
                          <RichContentBlocksView
                            source={opt}
                            imageAlt="Option"
                            contentDirection={formattedTextDir}
                            {...(isHighlighted && searchHighlightTerm ? { searchHighlightTerm } : {})}
                          />
                          {!opt.textContent?.trim()
                            && !opt.imageUrl?.trim()
                            && !opt.audioUrl?.trim()
                            && !opt.videoUrl?.trim()
                            && !opt.textFormatting?.trim() ? (
                            <span className="text-gray-700 dark:text-gray-300">Option {opt.displayOrder ?? opt.id}</span>
                          ) : null}
                        </button>
                      ))}
                  </div>
                ) : null}

                {!readOnlyPreview ? (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => handleCheck(q.id)}
                    disabled={isChecking || !selected?.trim()}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium dark:bg-amber-600 dark:hover:bg-amber-500"
                  >
                    {isChecking ? tcQ('exercise.checking') : tcQ('exercise.check')}
                  </button>
                </div>
                ) : null}

                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
                    result ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="min-h-0 overflow-hidden">
                    {result ? (
                      <div
                        dir={formattedTextDir}
                        className={`mt-3 p-4 rounded-lg flex items-start gap-3 ${
                          exerciseEnglishLocked ? 'text-start' : ''
                        } ${
                          result.correct
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                        }`}
                      >
                        {result.correct ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0 space-y-2">
                          <p className={`font-semibold text-sm ${result.correct ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                            {result.correct ? tcQ('exercise.correct') : tcQ('exercise.incorrect')}
                          </p>
                          {pickQuestionExplanation(result, questionContentLocale) ? (
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              <p className="font-medium text-gray-800 dark:text-gray-200 mb-1">
                                {tcQ('exercise.explanationLabel', { defaultValue: 'Explanation' })}
                              </p>
                              <FormattedTextBlock
                                textContent={pickQuestionExplanation(result, questionContentLocale)}
                                contentDirection={formattedTextDir}
                              />
                            </div>
                          ) : null}
                          {!result.correct && (() => {
                            const isPuzzle =
                              q.questionType === QuestionType.Puzzle ||
                              q.questionType === QuestionType.SortParagraphs;
                            if (isPuzzle && options.length > 0) {
                              const sortedCorrect = [...options].sort(
                                (a: any, b: any) =>
                                  (a.correctOrder ?? a.displayOrder ?? 0) - (b.correctOrder ?? b.displayOrder ?? 0)
                              );
                              const getPlain = (opt: any) => getOptionPlainText(opt, '');
                              const phrase = sortedCorrect.map(getPlain).filter(Boolean).join(' · ');
                              if (!phrase) return null;
                              return (
                                <div className="pt-2 border-t border-gray-200/50 dark:border-gray-600/50">
                                  <p className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-1">
                                    {tcQ('exercise.correctOrderLabel', { defaultValue: 'Correct order' })}
                                  </p>
                                  <p className="text-sm text-gray-700 dark:text-gray-300" dir={formattedTextDir}>
                                    {phrase}
                                  </p>
                                </div>
                              );
                            }
                            if (
                              q.questionType === QuestionType.Essay ||
                              q.questionType === QuestionType.FindTheWords ||
                              q.questionType === QuestionType.Crossword
                            ) {
                              return null;
                            }
                            const correctOpt = (q.questionOptions ?? []).find((o: any) => o.isCorrect);
                            if (!correctOpt) return null;
                            return (
                              <div className="pt-2 border-t border-gray-200/50 dark:border-gray-600/50" dir={formattedTextDir}>
                                <p className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-1">
                                  {tcQ('exercise.correctAnswerLabel', { defaultValue: 'Correct answer' })}
                                </p>
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                  <RichContentBlocksView
                                    source={correctOpt}
                                    imageAlt="Correct answer"
                                    contentDirection={formattedTextDir}
                                  />
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
          {hasMultiplePages ? (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 border-t border-gray-200 pt-3 dark:border-gray-700 sm:justify-between">
              <button
                type="button"
                onClick={() => setCurrentPageIndex((p) => Math.max(0, p - 1))}
                disabled={currentPageIndex === 0}
                className={`flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm sm:px-3 ${
                  currentPageIndex === 0
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <ArrowLeft className={navArrowClass('w-4 h-4')} />
                {tc('quiz.prevPage')}
              </button>
              <span className="text-center text-sm text-gray-600 dark:text-gray-400">
                {tc('quiz.pageOf', { current: currentPageIndex + 1, total: pages.length })}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPageIndex((p) => Math.min(pages.length - 1, p + 1))}
                disabled={currentPageIndex >= pages.length - 1}
                className={`flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm sm:px-3 ${
                  currentPageIndex >= pages.length - 1
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {tc('quiz.nextPage')}
                <ArrowRight className={navArrowClass('w-4 h-4')} />
              </button>
            </div>
          ) : null}
        </div>
      </div>
  );

  if (isInline) {
    return panel;
  }

  return portalExerciseModal(
    <LearnerSlideModalShell
      mounted={slideMounted}
      visible={slideVisible}
      panelRef={slidePanelRef}
      onBackdropClick={handleDismiss}
      onPanelTransitionEnd={handleExitTransitionEnd}
      panelClassName={EXERCISE_MODAL_PANEL_CLASS}
      ariaLabelledBy="exercise-modal-title"
      align="center"
    >
      {panel}
    </LearnerSlideModalShell>
  );
};
